import type { ConnectionGame, ConnectionPlayer } from 'types/connections';
import type {
  ConnectionPlayerProfileFieldsFragment,
  ConnectionSharedGameFieldsFragment,
} from 'types/generated/client';
import { SportsEnum } from 'types/generated/client';
import { getVerificationLevel } from 'types/verification';
import { buildGameTitle } from 'utils/shared/games/buildGameTitle';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import { mapCompetitiveness } from 'components/Find/mapCompetitiveness';
import type { PlayerGender } from 'components/Find/types';

/** Only the two values the audience check understands; anything else is "unknown". */
const toPlayerGender = (gender?: string | null): PlayerGender | null =>
  gender === 'MALE' || gender === 'FEMALE' ? gender : null;

/** A stored sport string, narrowed to the enum the badges take. */
const toSport = (defaultSport?: string | null): SportsEnum =>
  Object.values(SportsEnum).includes(defaultSport as SportsEnum)
    ? (defaultSport as SportsEnum)
    : SportsEnum.Pickleball;

export interface MapConnectionPlayerInput {
  profile: ConnectionPlayerProfileFieldsFragment | null | undefined;
  /** From `user_played_with`; 0 when the pair has no shared game. */
  gamesTogether?: number | null;
  isRequested?: boolean;
  pendingRequestId?: string | null;
  canRequest?: boolean;
  /** Fallback when the profile row is missing, so a row still has a stable key. */
  fallbackId: string;
}

/**
 * One `user_profiles` row as the connection rows render it. The skill level is the
 * player's rating for their own default sport, and stays null when they have not
 * set one — the badge is hidden rather than showing a made-up level.
 */
export const mapConnectionPlayer = ({
  profile,
  gamesTogether,
  isRequested = false,
  pendingRequestId = null,
  canRequest = true,
  fallbackId,
}: MapConnectionPlayerInput): ConnectionPlayer => {
  const sport = toSport(profile?.defaultSport);
  const skillLevel =
    sport === SportsEnum.Tennis
      ? profile?.tennisSkillLevel?.id
      : sport === SportsEnum.Padel
      ? profile?.padelSkillLevel?.id
      : profile?.pickleballSkillLevel?.id;

  return {
    id: profile?.id ?? fallbackId,
    fullName: profile?.preferredName || profile?.fullName || 'Player',
    username: profile?.username ?? '',
    profileImagePath: profile?.profileImagePath ?? null,
    skillLevel: skillLevel ?? null,
    gender: toPlayerGender(profile?.gender),
    sport,
    // The shield shows for Verified and Trusted alike; only "no level" hides it.
    isVerified: Boolean(
      getVerificationLevel({ identityVerifications: profile?.identityVerifications }),
    ),
    gamesTogether: gamesTogether ?? 0,
    isRequested: isRequested || Boolean(pendingRequestId),
    pendingRequestId,
    canRequest,
  };
};

/**
 * A shared past game as the request modal's card renders it. The title is rebuilt
 * with the same `buildGameTitle` the /find list uses, so the two read identically.
 */
export const mapSharedGame = (row: ConnectionSharedGameFieldsFragment): ConnectionGame | null => {
  const event = row.event;
  if (!event) return null;

  const group = event.groups[0];

  return {
    id: event.id,
    sport: event.sport,
    competitiveness: mapCompetitiveness(group?.competitiveness) ?? null,
    title: group
      ? buildGameTitle({
          teamType: group.teamType,
          selfRatedMinSkillLevel: group.selfRatedMinSkillLevel,
          selfRatedMaxSkillLevel: group.selfRatedMaxSkillLevel,
          ratingScales: group.ratingScales,
        })
      : '',
    startDateTime: event.startDateTime,
    endDateTime: event.endDateTime,
    timezone: resolveVenueTimeZone(event),
    venueName: event.venue?.title || event.displayLocation || '',
    priceUnitAmount: event.courtCostPerPlayerUnitAmount ?? null,
  };
};
